import { planAtLeast, WORKFLOW_STEP_MIN_PLAN } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Icon } from "@sagot/ui";
import { useWorkspaceDetails } from "../../../../session/workspace-context";
import type { HttpRequestStep } from "../types";
import { ConnectorEndpointField } from "./connector-endpoint-field";
import { ConnectorMapping } from "./connector-mapping";
import { CONNECTOR_EXAMPLES, timeoutCapOf } from "./connector-model";
import { RequestBody, RequestLine, TimeoutField } from "./connector-request-fields";
import { ConnectorTestPanel } from "./connector-test-panel";
import { ExitsEditor } from "./exits-editor";
import type { FlowContext } from "./flow-context";
import { VariableList, type FormProps } from "./webhook-form";

const C = "engage.workflows.connector";
const CONNECTOR_PLAN = WORKFLOW_STEP_MIN_PLAN.http_request ?? "pro";

/** Where the request runs decides how long it may take: behind the scenes, or while the visitor waits. */
function RunsNote({ facing }: { facing: boolean }) {
  const { t } = useTranslation();
  const seconds = timeoutCapOf(facing) / 1000;
  return (
    <p className="flex items-start gap-2 rounded-md bg-info-soft p-3 text-xs text-fg-muted" data-testid="connector-runs-note">
      <Icon name="info" className="mt-0.5 size-3.5 shrink-0 text-info" />
      {t(facing ? `${C}.runsFacing` : `${C}.runsBehind`, { seconds })}
    </p>
  );
}

/**
 * Data connector: calls an endpoint that is set to "Only from workflows" (its address, signing secret and credentials stay
 * on the endpoint), and saves fields of the JSON answer into run variables. Success and failure each have an exit.
 */
export function ConnectorForm({ step, disabled, onChange, paths, canAddPath, facing = false }: FormProps<HttpRequestStep> & Pick<FlowContext, "paths" | "canAddPath" | "facing">) {
  const { t } = useTranslation();
  const plan = useWorkspaceDetails().data?.plan;
  const locked = plan !== undefined && !planAtLeast(plan, CONNECTOR_PLAN);
  const planName = t(`console.plans.${CONNECTOR_PLAN}`);
  return (
    <div className="space-y-5">
      {locked ? (
        <p className="flex items-center gap-2 rounded-md bg-warning-soft p-3 text-xs" role="note" data-testid="connector-plan-lock">
          <Icon name="lock" className="size-4 shrink-0" />
          {t(`${C}.plan`, { plan: planName })}
        </p>
      ) : null}
      <RunsNote facing={facing} />
      <ConnectorEndpointField value={step.endpoint_id} disabled={disabled} onChange={(endpoint_id) => { onChange({ ...step, endpoint_id }); }} />
      <RequestLine step={step} disabled={disabled} onChange={onChange} />
      <RequestBody step={step} disabled={disabled} onChange={onChange} />
      <TimeoutField step={step} disabled={disabled} facing={facing} onChange={onChange} />
      <div>
        <VariableList />
        <p className="mt-1 text-xs text-fg-subtle">{t(`${C}.fields.variablesEarlier`, { example: CONNECTOR_EXAMPLES.variable })}</p>
      </div>
      <ConnectorMapping step={step} disabled={disabled} onChange={onChange} />
      <ConnectorTestPanel step={step} locked={locked} planName={planName} disabled={disabled} onChange={onChange} />
      <ExitsEditor step={step} disabled={disabled} onChange={onChange} paths={paths} canAddPath={canAddPath} />
    </div>
  );
}
